import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type DateRangePreset = 'thisYear' | 'lastYear' | 'allTime' | 'custom';

interface MonthlyRevenue {
  month: string;
  year: number;
  revenue: number;
  displayLabel: string;
}

interface YearTotal {
  year: number;
  total: number;
}

export const RevenueInsights: React.FC = () => {
  const completedOrders = useSelector((state: RootState) => state.orders.completedOrders);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('thisYear');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate date range based on preset or custom dates
  const dateRange = useMemo(() => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    switch (selectedPreset) {
      case 'thisYear':
        startDate = new Date(currentYear, 0, 1);
        break;
      case 'lastYear':
        startDate = new Date(currentYear - 1, 0, 1);
        endDate = new Date(currentYear - 1, 11, 31, 23, 59, 59);
        break;
      case 'allTime':
        startDate = new Date(0); // Beginning of time
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          // Parse date strings as local dates (not UTC) to match behavior of other presets
          const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);

          startDate = new Date(startYear, startMonth - 1, startDay);
          endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
        } else {
          // Default to this year if custom dates not set
          startDate = new Date(currentYear, 0, 1);
        }
        break;
      default:
        startDate = new Date(currentYear, 0, 1);
    }

    return { startDate, endDate };
  }, [selectedPreset, customStartDate, customEndDate, currentYear]);

  // Calculate monthly revenue data
  const { monthlyData, yearTotals, chartData, years, spanMultipleYears } = useMemo(() => {
    // Filter completed orders within date range that have completionDate and pricing
    const ordersInRange = completedOrders.filter(order => {
      if (!order.completionDate || !order.pricing) return false;

      const completionDate = new Date(order.completionDate);
      return completionDate >= dateRange.startDate && completionDate <= dateRange.endDate;
    });

    // Group by year and month
    const revenueByMonth = new Map<string, number>();

    ordersInRange.forEach(order => {
      if (!order.completionDate || !order.pricing) return;

      const date = new Date(order.completionDate);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11
      const key = `${year}-${month}`;

      const current = revenueByMonth.get(key) || 0;
      revenueByMonth.set(key, current + order.pricing.total);
    });

    // Convert to array and sort by date
    const monthlyRevenue: MonthlyRevenue[] = [];
    revenueByMonth.forEach((revenue, key) => {
      const [yearStr, monthStr] = key.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      monthlyRevenue.push({
        month: monthNames[month],
        year,
        revenue,
        displayLabel: `${monthNames[month]} ${year}`
      });
    });

    monthlyRevenue.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames.indexOf(a.month) - monthNames.indexOf(b.month);
    });

    // Calculate year totals
    const yearTotalsMap = new Map<number, number>();
    monthlyRevenue.forEach(item => {
      const current = yearTotalsMap.get(item.year) || 0;
      yearTotalsMap.set(item.year, current + item.revenue);
    });

    const yearTotals: YearTotal[] = Array.from(yearTotalsMap.entries())
      .map(([year, total]) => ({ year, total }))
      .sort((a, b) => a.year - b.year);

    // Prepare chart data - group by year for multi-year ranges
    const years = Array.from(new Set(monthlyRevenue.map(item => item.year))).sort();
    const spanMultipleYears = years.length > 1;

    let chartData: any[];

    if (spanMultipleYears) {
      // Create chart data with separate bars for each year
      const monthSet = new Set(monthlyRevenue.map(item => item.month));
      chartData = Array.from(monthSet).map(month => {
        const dataPoint: any = { month };
        years.forEach(year => {
          const item = monthlyRevenue.find(m => m.month === month && m.year === year);
          dataPoint[year.toString()] = item ? item.revenue : 0;
        });
        return dataPoint;
      });
    } else {
      // Single year - simple format
      chartData = monthlyRevenue.map(item => ({
        month: item.month,
        revenue: item.revenue
      }));
    }

    return { monthlyData: monthlyRevenue, yearTotals, chartData, years, spanMultipleYears };
  }, [completedOrders, dateRange]);

  // Ensure full date range is displayed for all time period options
  const finalChartData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // For "This Year" and "Last Year", always show all 12 months
    if (selectedPreset === 'thisYear' || selectedPreset === 'lastYear') {
      const targetYear = selectedPreset === 'thisYear' ? currentYear : currentYear - 1;

      if (spanMultipleYears) {
        // Multi-year chart format
        return monthNames.map(month => {
          const existingData = chartData.find(item => item.month === month);
          const dataPoint: any = { month };

          // Ensure all years have data (even if zero)
          years.forEach(year => {
            dataPoint[year.toString()] = existingData?.[year.toString()] || 0;
          });

          return dataPoint;
        });
      } else {
        // Single year format
        return monthNames.map(month => {
          const existingData = chartData.find(item => item.month === month);
          return {
            month,
            revenue: existingData ? existingData.revenue : 0
          };
        });
      }
    }

    // For "All Time", show all months from first order to current date
    if (selectedPreset === 'allTime' && completedOrders.length > 0) {
      // Find the earliest completion date
      const ordersWithDates = completedOrders
        .filter(order => order.completionDate)
        .map(order => new Date(order.completionDate!));

      if (ordersWithDates.length === 0) {
        return chartData;
      }

      const earliestDate = new Date(Math.min(...ordersWithDates.map(d => d.getTime())));
      const latestDate = new Date();

      const startYear = earliestDate.getFullYear();
      const startMonth = earliestDate.getMonth();
      const endYear = latestDate.getFullYear();
      const endMonth = latestDate.getMonth();

      // Generate all months between start and end
      const allMonths: any[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const firstMonth = year === startYear ? startMonth : 0;
        const lastMonth = year === endYear ? endMonth : 11;

        for (let monthIdx = firstMonth; monthIdx <= lastMonth; monthIdx++) {
          const month = monthNames[monthIdx];

          if (spanMultipleYears) {
            // Multi-year format
            const existingData = chartData.find(item => item.month === month);
            const dataPoint: any = { month };

            years.forEach(y => {
              dataPoint[y.toString()] = existingData?.[y.toString()] || 0;
            });

            allMonths.push(dataPoint);
          } else {
            // Single year format
            const existingData = chartData.find(item => item.month === month);
            allMonths.push({
              month,
              revenue: existingData ? existingData.revenue : 0
            });
          }
        }
      }

      return allMonths;
    }

    // For "Custom Range", show all months within the selected range
    if (selectedPreset === 'custom' && customStartDate && customEndDate) {
      // Parse date strings as local dates (not UTC) to match behavior of other presets
      const [startYearNum, startMonthNum, startDay] = customStartDate.split('-').map(Number);
      const [endYearNum, endMonthNum, endDay] = customEndDate.split('-').map(Number);

      const startDate = new Date(startYearNum, startMonthNum - 1, startDay);
      const endDate = new Date(endYearNum, endMonthNum - 1, endDay);

      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth();

      // Generate all months between start and end
      const allMonths: any[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const firstMonth = year === startYear ? startMonth : 0;
        const lastMonth = year === endYear ? endMonth : 11;

        for (let monthIdx = firstMonth; monthIdx <= lastMonth; monthIdx++) {
          const month = monthNames[monthIdx];

          if (spanMultipleYears) {
            // Multi-year format
            const existingData = chartData.find(item => item.month === month);
            const dataPoint: any = { month };

            years.forEach(y => {
              dataPoint[y.toString()] = existingData?.[y.toString()] || 0;
            });

            allMonths.push(dataPoint);
          } else {
            // Single year format
            const existingData = chartData.find(item => item.month === month);
            allMonths.push({
              month,
              revenue: existingData ? existingData.revenue : 0
            });
          }
        }
      }

      return allMonths;
    }

    return chartData;
  }, [
    chartData,
    selectedPreset,
    spanMultipleYears,
    years,
    currentYear,
    completedOrders,
    customStartDate,
    customEndDate
  ]);

  // Format currency
  const formatCurrency = (value: number): string => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Year colors for multi-year view
  const yearColors: { [key: string]: string } = {
    [currentYear.toString()]: '#4CAF50',
    [(currentYear - 1).toString()]: '#2196F3',
    [(currentYear - 2).toString()]: '#FF9800',
    [(currentYear - 3).toString()]: '#9C27B0'
  };

  // Format date range for header
  const formatDateRange = (): string => {
    const formatDate = (date: Date): string => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };

    return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
  };

  return (
    <div style={{ marginBottom: '40px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSelectedPreset('thisYear')}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedPreset === 'thisYear' ? '#4CAF50' : '#f0f0f0',
              color: selectedPreset === 'thisYear' ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            This Year
          </button>
          <button
            onClick={() => setSelectedPreset('lastYear')}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedPreset === 'lastYear' ? '#4CAF50' : '#f0f0f0',
              color: selectedPreset === 'lastYear' ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Last Year
          </button>
          <button
            onClick={() => setSelectedPreset('allTime')}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedPreset === 'allTime' ? '#4CAF50' : '#f0f0f0',
              color: selectedPreset === 'allTime' ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            All Time
          </button>
        </div>
      </div>

      {selectedPreset === 'custom' && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '20px',
            alignItems: 'center'
          }}
        >
          <label style={{ color: '#666' }}>
            From:
            <input
              type="date"
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              style={{
                marginLeft: '8px',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </label>
          <label style={{ color: '#666' }}>
            To:
            <input
              type="date"
              value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
              style={{
                marginLeft: '8px',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </label>
        </div>
      )}

      <button
        onClick={() => setSelectedPreset('custom')}
        style={{
          padding: '8px 16px',
          backgroundColor: selectedPreset === 'custom' ? '#4CAF50' : '#f0f0f0',
          color: selectedPreset === 'custom' ? 'white' : '#333',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px'
        }}
      >
        Custom Range
      </button>

      <div style={{ marginBottom: '20px' }}>
        <h2
          style={{
            fontSize: '1.5rem',
            color: '#333',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}
        >
          Revenue
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.9rem',
              color: '#666',
              fontWeight: 400
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ flexShrink: 0 }}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDateRange()}
          </span>
        </h2>
        {yearTotals.length > 0 && (
          <div style={{ fontSize: '1.1rem', color: '#666', fontWeight: 500 }}>
            {yearTotals.map((yt, index) => (
              <span key={yt.year}>
                {formatCurrency(yt.total)} {yt.year}
                {index < yearTotals.length - 1 ? ' / ' : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {finalChartData.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999'
            }}
          >
            No revenue data available for the selected period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={finalChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip
                formatter={(value: number | undefined) => (value !== undefined ? `$${value.toFixed(2)}` : '$0.00')}
                contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
              />
              {spanMultipleYears ? (
                <>
                  <Legend />
                  {years.map(year => (
                    <Bar
                      key={year}
                      dataKey={year.toString()}
                      fill={yearColors[year.toString()] || '#4CAF50'}
                      name={year.toString()}
                    />
                  ))}
                </>
              ) : (
                <Bar dataKey="revenue" fill="#4CAF50" name="Revenue" />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
